import { Button, Card, Empty, Flex, Modal, ModalProps, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useTreatmentServiceGetListNewbornInvestigationEndpoint } from 'src/api/queries'
import {
  NewbornPlanInvestigationItemDto,
  NewbornPlanInvestigationStatus
} from 'src/api/requests'
import { Text } from 'src/components'
import { dayMonthYearBackSlash, dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import {
  NewBornPeriodStatusTag,
  PregnancyInvestigationsStatusTag
} from '../PregnancyStatusTag'

import { PregnancyNewBornInvestigationsModal } from './PregnancyNewBornInvestigationsModal'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const PersonNewBornDetailsModal = ({ id, onCancel }: Props) => {
  const { t } = useLocales()

  const [modalState, setModalState] = useState<{
    id?: string
    data?: NewbornPlanInvestigationItemDto | null
  }>({ data: null, id: String(id) })

  const { data: investigations } =
    useTreatmentServiceGetListNewbornInvestigationEndpoint({
      ids: [String(id)],
      pageNumber: 1
    })

  const onCancelModal = () => {
    setModalState({ data: null, id: '' })
  }

  const columns: ColumnsType<NewbornPlanInvestigationItemDto> = [
    {
      key: 'created date',
      title: t('pregnancy.investigation_limit_date'),
      render: ({ periodDate }: { periodDate: string }) =>
        dayjs.utc(periodDate).tz().format(dayMonthYearBackSlash)
    },
    {
      key: 'pregnancyPeriod',
      title: t('pregnancy.monitoring_period'),
      render: (_, { newbornPeriod }) => (
        <NewBornPeriodStatusTag status={newbornPeriod} />
      )
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => (
        <PregnancyInvestigationsStatusTag status={status} />
      )
    },
    {
      key: 'demand',
      title: t('pregnancy.newborn_investigation_status.demand'),
      render: (_, { investigationDemand }) =>
        investigationDemand ? (
          <Flex vertical gap={2}>
            <span>{investigationDemand.code}</span>
            {investigationDemand.status && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(`tests.statuses.${investigationDemand.status}`)}
              </Text>
            )}
          </Flex>
        ) : (
          '-'
        )
    },
    {
      key: 'result',
      title: t('pregnancy.newborn_investigation_status.result'),
      render: (_, { hasResult, resultDate, resultTitle }) =>
        hasResult ? (
          <Flex vertical gap={2} align="flex-start">
            <Tag color="success">
              {t('pregnancy.newborn_plan_result.present')}
            </Tag>
            {resultTitle && <span>{resultTitle}</span>}
            {resultDate && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs.utc(resultDate).tz().format(dayMonthYearFormatWithDot)}
              </Text>
            )}
          </Flex>
        ) : (
          <Tag>{t('pregnancy.newborn_plan_result.absent')}</Tag>
        )
    },
    {
      key: 'actions',
      title: t('common.action'),
      render: (record) => {
        // An expired period (e.g. a late birth registration) can still be requested.
        const isToDoStatus =
          record?.status === NewbornPlanInvestigationStatus.TO_DO ||
          record?.status ===
            NewbornPlanInvestigationStatus.NEED_TO_INVESTIGATE ||
          record?.status === NewbornPlanInvestigationStatus.EXPIRED

        return isToDoStatus ? (
          <Button
            type="primary"
            onClick={() => setModalState({ data: record })}
          >
            {t('pregnancy_monitoring.add')}
          </Button>
        ) : null
      }
    }
  ]

  return (
    <Modal
      open
      onCancel={onCancel}
      width={920}
      maskClosable={false}
      footer={[
        <Flex key="back" justify="center">
          <Button onClick={onCancel} type="primary">
            {t('investigations.close')}
          </Button>
        </Flex>
      ]}
      title={
        <Text weight={700} size="xxl">
          {t('common.details')}
        </Text>
      }
    >
      <Card title={t('persons.person_investigations')}>
        {investigations?.data?.length ? (
          <Flex vertical gap={24}>
            <Table
              rowKey="id"
              bordered
              columns={columns}
              scroll={{ x: 'max-content' }}
              dataSource={investigations?.data}
              pagination={false}
            />
          </Flex>
        ) : (
          <Empty />
        )}
      </Card>

      {modalState.data && (
        <PregnancyNewBornInvestigationsModal
          onCancel={onCancelModal}
          data={{
            id: String(id),
            data: modalState.data
          }}
        />
      )}
    </Modal>
  )
}
